/**
 * One past call (route /calls/:callId): what was asked, how it went, and the
 * transcript, in the same layout as the live view on the call page.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CallDetail as CallDetailData,
  TranscriptMessage,
  callCountry,
  fmtCallDateTime,
  fmtCallTime,
  getCall,
  getCallTranscript,
} from './callsApi';
import { COUNTRIES, formatNational, nationalDigits } from './phone';
import { Prefill, savePrefill } from './prefill';
import { CALL_MINUTES_PER_CREDIT, TimeLimitBanner, Transcript, TranscriptCard, wasCutOffAtTimeLimit } from './Transcript';
import './App.css';
import './Calls.css';

export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m} min ${s} s` : `${s} s`;
}

export function statusLabel(call: CallDetailData): string {
  if (call.answeredBy === 'machine') return 'Answering machine';
  if (call.answeredBy === 'fax') return 'Fax machine';
  switch (call.status) {
    case 'completed':
      return 'Completed';
    case 'no-answer':
    case 'no_answer':
      return 'No answer';
    case 'busy':
      return 'Busy';
    case 'failed':
      return 'Failed';
    case 'canceled':
      return 'Canceled';
    case null:
    case undefined:
      return 'In progress';
    default:
      return call.status;
  }
}

export function creditLabel(call: CallDetailData): string {
  if (call.creditDeducted) {
    const n = call.creditsDeducted ?? 1;
    return `${n} credit${n === 1 ? '' : 's'}`;
  }
  if (call.reason === 'no_conversation') return 'Not charged · no conversation';
  if (call.reason === 'insufficient_credits') return 'Not charged · no credits left';
  if (!call.status) return 'Pending';
  return 'Not charged';
}

/** What "Copy to new call" puts in the form: the same number, language and prompt. */
export function copyToNewCallPrefill(call: CallDetailData): Prefill {
  const country = callCountry(call);
  const prefill: Prefill = {
    country,
    language: call.language ?? COUNTRIES[country].defaultLanguage,
  };
  if (call.toNumber) prefill.phone = formatNational(country, nationalDigits(country, call.toNumber));
  const prompt = call.prompt || call.objective;
  if (prompt) prefill.prompt = prompt;
  return prefill;
}

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback;
}

function CallDetail() {
  const { callId = '' } = useParams();
  const navigate = useNavigate();
  const [call, setCall] = useState<CallDetailData | null>(null);
  const [error, setError] = useState('');
  const [transcript, setTranscript] = useState<TranscriptMessage[] | null>(null);
  const [transcriptError, setTranscriptError] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCall(null);
    setError('');
    setTranscript(null);
    setTranscriptError('');
    setShowPrompt(false);
    getCall(callId)
      .then((data) => {
        if (cancelled) return;
        setCall(data);
        if (!data.hasTranscript) {
          setTranscript([]);
          return;
        }
        getCallTranscript(callId)
          .then((lines) => !cancelled && setTranscript(lines))
          .catch((e) => !cancelled && setTranscriptError(errorMessage(e, 'Could not load the transcript')));
      })
      .catch((e) => !cancelled && setError(errorMessage(e, 'Could not load the call')));
    return () => {
      cancelled = true;
    };
  }, [callId]);

  const lines = (transcript ?? []).map((m) => ({
    role: m.role,
    event: m.event,
    content: m.content,
    timestamp: m.timestamp && call ? fmtCallTime(m.timestamp, call) : undefined,
  }));
  // Older calls have no flag on the record; their transcript still says.
  const cutOff = !!call && (call.timeLimitReached || wasCutOffAtTimeLimit(lines));
  const limitMinutes = Math.round((call?.maxDurationSeconds ?? CALL_MINUTES_PER_CREDIT * 60) / 60);

  return (
    <div className="App">
      <header className="App-header call-detail">
        {error && <div className="call-detail-error">{error}</div>}
        {!call && !error && <div className="call-detail-muted">Loading…</div>}
        {call && (
          <>
            <div className="call-detail-head">
              <h1 className="call-detail-title">{call.title}</h1>
              <dl className="call-detail-meta">
                <div>
                  <dt>Number</dt>
                  <dd>{call.toNumber || '—'}</dd>
                </div>
                <div>
                  <dt>When</dt>
                  <dd>{fmtCallDateTime(call.initiatedAt, call)}</dd>
                </div>
                <div>
                  <dt>Duration</dt>
                  <dd>{fmtDuration(call.duration)}</dd>
                </div>
                <div>
                  <dt>Outcome</dt>
                  <dd>{statusLabel(call)}</dd>
                </div>
                <div>
                  <dt>Charged</dt>
                  <dd>{creditLabel(call)}</dd>
                </div>
                {call.maxDurationSeconds && (
                  <div>
                    <dt>Limit</dt>
                    <dd>{limitMinutes} min</dd>
                  </div>
                )}
                {call.source && call.source !== 'web' && (
                  <div>
                    <dt>Made via</dt>
                    <dd>{call.source.toUpperCase()}</dd>
                  </div>
                )}
              </dl>
              {(call.prompt || call.objective) && (
                <div className="call-detail-prompt">
                  <button
                    type="button"
                    className="call-detail-disclosure"
                    aria-expanded={showPrompt}
                    onClick={() => setShowPrompt((v) => !v)}
                  >
                    <span className={`call-detail-triangle${showPrompt ? ' open' : ''}`} aria-hidden="true" />
                    Prompt
                  </button>
                  {showPrompt && <pre>{call.prompt || call.objective}</pre>}
                </div>
              )}
              <button
                type="button"
                className="copy-call-button"
                onClick={() => {
                  savePrefill(copyToNewCallPrefill(call));
                  navigate('/');
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="9" y="9" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Copy to new call
              </button>
            </div>

            <TranscriptCard title="Transcript">
              {cutOff && <TimeLimitBanner limitMinutes={limitMinutes} />}
              {transcriptError ? (
                <div className="call-detail-error">{transcriptError}</div>
              ) : (
                <Transcript
                  messages={lines}
                  maxHeight="none"
                  empty={
                    transcript === null
                      ? 'Loading…'
                      : call.hasTranscript
                        ? 'Empty transcript'
                        : call.status
                          ? 'No conversation was recorded for this call.'
                          : 'The transcript appears once the call ends.'
                  }
                />
              )}
            </TranscriptCard>
          </>
        )}
      </header>
    </div>
  );
}

export default CallDetail;
